const EXERCISE_SETS_KEY = "exerciseSets";
const EXERCISE_LOG_KEY = "exerciseLog";
const CUSTOM_EXERCISES_KEY = "customExercises";

/**
 * Gets all data from localStorage or returns an empty object.
 * @param {string} key Key to retrieve data from localStorage.
 * @returns {Object} Data from localStorage.
 */
function getDataFromLocalStorage(key) {
    try {
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : {};
    } catch (error) {
        console.error(`Error retrieving data from localStorage for key "${key}":`, error);
        return {};
    }
}

/**
 * Saves data to localStorage.
 * @param {string} key Key for saving.
 * @param {Object} data Data to save.
 * @returns {boolean} True if save was successful, false otherwise.
 */
function saveDataToLocalStorage(key, data) {
    try {
        localStorage.setItem(key, JSON.stringify(data));
        return true;
    } catch (error) {
        console.error(`Error saving data to localStorage for key "${key}":`, error);
        // Show a user-friendly message if storage quota is exceeded
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
            alert('Storage quota exceeded. Some data may not be saved. Try clearing browser data.');
        }
        return false;
    }
}

/**
 * Gets all exercise data from localStorage.
 * @returns {Object} All exercise data.
 */
export function getAllExerciseSets() {
    return getDataFromLocalStorage(EXERCISE_SETS_KEY);
}

/**
 * Saves exercise data to localStorage.
 * @param {Object} exerciseSets Exercise data to save.
 * @returns {boolean} True if save was successful, false otherwise.
 */
export function saveAllExerciseSets(exerciseSets) {
    return saveDataToLocalStorage(EXERCISE_SETS_KEY, exerciseSets);
}

/**
 * Gets sets for a specific exercise.
 * @param {string} exerciseId Exercise ID.
 * @returns {Array} List of sets for the exercise.
 */
export function getExerciseSets(exerciseId) {
    const allExercisesSets = getAllExerciseSets();
    return allExercisesSets[exerciseId] || [];
}

/**
 * Saves sets for a specific exercise.
 * @param {string} exerciseId Exercise ID.
 * @param {Array} sets List of sets to save.
 * @returns {boolean} True if save was successful, false otherwise.
 */
export function saveExerciseSets(exerciseId, sets) {
    const allExercisesSets = getAllExerciseSets();
    allExercisesSets[exerciseId] = sets;
    return saveAllExerciseSets(allExercisesSets);
}

/**
 * Gets all exercise data by dates from localStorage.
 * @returns {Object} Exercise data grouped by dates.
 */
export function getExerciseLog() {
    return getDataFromLocalStorage(EXERCISE_LOG_KEY);
}

/**
 * Saves exercise data by dates to localStorage.
 * @param {Object} log Data to save.
 * @returns {boolean} True if save was successful, false otherwise.
 */
export function saveExerciseLog(log) {
    return saveDataToLocalStorage(EXERCISE_LOG_KEY, log);
}

/**
 * Gets exercise data for a specific date.
 * @param {string} date Date in "YYYY-MM-DD" format.
 * @returns {Object} Exercise data for the specific date.
 */
export function getExerciseLogByDate(date) {
    const log = getExerciseLog();
    return log[date] || {};
}

/**
 * Saves exercise data for a specific date.
 * @param {string} date Date in "YYYY-MM-DD" format.
 * @param {Object} data Data to save.
 * @returns {boolean} True if save was successful, false otherwise.
 */
export function saveExerciseLogByDate(date, data) {
    const log = getExerciseLog();
    log[date] = data;
    return saveExerciseLog(log);
}

/**
 * Gets custom exercises created by the user.
 * @returns {Array} List of custom exercises.
 */
export function getCustomExercises() {
    const data = getDataFromLocalStorage(CUSTOM_EXERCISES_KEY);
    return Array.isArray(data) ? data : [];
}

/**
 * Saves custom exercises created by the user.
 * @param {Array} customExercises List of custom exercises.
 * @returns {boolean} True if save was successful, false otherwise.
 */
export function saveCustomExercises(customExercises) {
    return saveDataToLocalStorage(
        CUSTOM_EXERCISES_KEY,
        Array.isArray(customExercises) ? customExercises : []
    );
}

/**
 * Exports all user training data as a downloadable JSON file.
 * @returns {boolean} True if export was successful, false otherwise.
 */
export function exportTrainingData() {
    try {
        // Collect all user data
        const exerciseLog = getExerciseLog();
        const exerciseSets = getAllExerciseSets();
        
        // Create a data object with all user training information
        const exportData = {
            exerciseLog,
            exerciseSets,
            exportDate: new Date().toISOString()
        };
        
        // Convert to JSON string
        const jsonData = JSON.stringify(exportData, null, 2);
        
        // Create a Blob with the JSON data
        const blob = new Blob([jsonData], { type: 'application/json' });
        
        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        // Set link properties
        link.href = url;
        link.download = `workout-data-${new Date().toISOString().split('T')[0]}.json`;
        
        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Release the URL object
        URL.revokeObjectURL(url);
        
        return true;
    } catch (error) {
        console.error('Error exporting training data:', error);
        return false;
    }
}

/**
 * Imports training data from a JSON file and replaces current data.
 * @returns {Promise<boolean>} Promise resolving to true if import was successful, false otherwise.
 */
export function importTrainingData() {
    return new Promise((resolve) => {
        try {
            // Create a file input element
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'application/json';
            
            // Handle file selection
            fileInput.onchange = (event) => {
                const file = event.target.files[0];
                if (!file) {
                    resolve(false);
                    return;
                }
                
                const reader = new FileReader();
                
                reader.onload = (e) => {
                    try {
                        // Parse the JSON data
                        const importedData = JSON.parse(e.target.result);
                        
                        // Validate the data structure
                        if (!importedData.exerciseLog || !importedData.exerciseSets) {
                            alert('Invalid data format. The file does not contain valid training data.');
                            resolve(false);
                            return;
                        }
                        
                        // Save the imported data to localStorage
                        const saveLogSuccess = saveExerciseLog(importedData.exerciseLog);
                        const saveSetsSuccess = saveAllExerciseSets(importedData.exerciseSets);
                        
                        if (saveLogSuccess && saveSetsSuccess) {
                            alert('Training data imported successfully. The page will reload to reflect the changes.');
                            // Reload the page to reflect the imported data
                            window.location.reload();
                            resolve(true);
                        } else {
                            alert('Error saving imported data to localStorage.');
                            resolve(false);
                        }
                    } catch (parseError) {
                        console.error('Error parsing imported data:', parseError);
                        alert('Error parsing the imported file. Please ensure it is a valid JSON file.');
                        resolve(false);
                    }
                };
                
                reader.onerror = () => {
                    console.error('Error reading file');
                    alert('Error reading the file. Please try again.');
                    resolve(false);
                };
                
                // Read the file as text
                reader.readAsText(file);
            };
            
            // Trigger click to open file dialog
            fileInput.click();
        } catch (error) {
            console.error('Error importing training data:', error);
            alert('An error occurred while trying to import training data.');
            resolve(false);
        }
    });
}
